import { P } from 'components'
import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import { generateUniqueId } from 'helpers'
import { useSnackbar } from 'notistack'
import { useCallback, useMemo, useState } from 'react'
import { ArrowLeft, Edit2, Plus, Trash2 } from 'react-feather'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Card, CardBody, Col, Row, Spinner } from 'reactstrap'
import { formatInputNumber } from 'utility/Utils'
import { URL_REPORT_PNBP, URL_WORKPLACE } from 'utils/pathUrl'
import TableCombinedContent from './DetailCombinedContent'
import ModalAddItem from './ModalAddItem'
import ModalLedger from './ModalLedger'
import {
  useAddReportMutation,
  useFetchConfigurationQuery,
  useFetchReportsQuery,
} from './reportPnbpListApiSlice'
import './styles.css'

const DetailReportPnbp = () => {
  const { data: reports, refetch, isFetching } = useFetchReportsQuery()
  const { data, isLoading } = useFetchConfigurationQuery()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get('id')

  const [editDataItem, setEditDataItem] = useState(null)

  const isEditMode = Boolean(editDataItem)

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [isOpenModalAddItem, setIsOpenModalAddItem] = useState(false)

  const [isOpenModalLedger, setIsOpenModalLedger] = useState(false)

  const toggleModalLedger = () => {
    setIsOpenModalLedger((prev) => !prev)
    resetState()
  }

  const toggleModalDelete = () => {
    setIsOpenDeleteModal((prev) => !prev)
    setEditDataItem(null)
  }
  const toggleModalAddItem = () => {
    setIsOpenModalAddItem((prev) => !prev)
    setEditDataItem(null)

    setPayload({
      id: id,
      title: findReport?.title,
      periode: findReport?.periode,
      status: findReport?.status,
      itemConfigurations: [
        {
          type: null,
          commodity: null,
          psdh: null,
          unit: null,
          volume: null,
          total: null,
        },
      ],
    })
  }

  const [addReport, { isLoading: isAdding }] = useAddReportMutation()

  const findReport = reports?.find((report) => report?.id === id)

  const [payload, setPayload] = useState({
    id: id,
    title: '',
    periode: '',
    status: 'Draft',
    itemConfigurations: [
      {
        type: null,
        commodity: null,
        psdh: null,
        unit: null,
        volume: null,
        total: null,
      },
    ],
  })

  // HANDLE TYPE
  const typeOptions =
    data?.map((item) => ({
      label: item?.type,
      value: item?.type,
    })) ?? []
  const handleSelectType = (type) => {
    const selectedType = data?.find((item) => item.type === type?.value)
    setPayload((prev) => ({
      ...prev,
      itemConfigurations: [
        {
          ...prev.itemConfigurations[0],
          type: type,
          commodity: null,
          psdh: null,
          unit: null,
          volume: null,
        },
      ],
    }))
  }

  // HANDLE COMMODITY
  const commodityOptions = useMemo(() => {
    const type = payload.itemConfigurations?.[0]?.type?.label
    if (!type || !data) return []

    const selectedType = data?.find((item) => item.type === type)
    return (
      selectedType?.commodities?.map((item) => ({
        ...item,
        label: item?.commodity?.label,
        value: item?.commodity?.label,
        image: item?.commodity?.image,
      })) ?? []
    )
  }, [payload, data])
  const handleSelectCommodity = (commodity) => {
    setPayload((prev) => ({
      ...prev,
      itemConfigurations: [
        {
          ...prev.itemConfigurations[0],
          commodity: commodity,
          psdh: commodity?.psdh,
          unit: commodity?.unit,
          total: prev.itemConfigurations[0]?.volume * commodity?.psdh,
        },
      ],
    }))
  }

  // HANDLE VOLUME
  const handleVolumeChange = useCallback((value) => {
    const replaceValue = value.replace(/[^\d,]/g, '')

    const [integerPart, decimalPart] = replaceValue.split(',')

    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')

    const formattedValue = decimalPart
      ? `${formattedInteger},${decimalPart}`
      : formattedInteger

    const numericValue =
      parseFloat(formattedValue.replace(/\./g, '').replace(',', '.')) || null

    setPayload((prev) => ({
      ...prev,
      itemConfigurations: [
        {
          ...prev.itemConfigurations[0],
          volume: numericValue,
          total: numericValue * prev.itemConfigurations[0]?.psdh,
        },
      ],
    }))
  }, [])

  // RESET STATE
  const resetState = () => {
    setPayload({
      id: null,
      title: '',
      periode: '',
      status: 'Draft',
      itemConfigurations: [
        {
          type: null,
          commodity: null,
          psdh: null,
          unit: null,
          volume: null,
          total: null,
        },
      ],
    })
  }

  // HANDLE IS FORM INVALID
  const isFormInvalid =
    !payload.itemConfigurations?.[0]?.type ||
    !payload.itemConfigurations?.[0]?.commodity ||
    (!payload.itemConfigurations?.[0]?.volume &&
      payload.itemConfigurations?.[0]?.volume !== 0) ||
    (!payload.itemConfigurations?.[0]?.total &&
      payload.itemConfigurations?.[0]?.total !== 0) ||
    !payload.itemConfigurations?.[0]?.psdh

  // HANDLE SUBMIT
  const handleSubmit = async () => {
    try {
      if (isFormInvalid) {
        enqueueSnackbar('Lengkapi data terlebih dahulu', {
          variant: 'error',
        })
        return
      }

      const uniqueId = generateUniqueId()

      // FIND THE EXISTING REPORT
      const existingReport = reports?.find((report) => report.id === id)

      let updatedItemConfigurations = []

      if (isEditMode) {
        // IF EDITING, REPLACE THE MATCHING ITEM AND KEEP OTHERS
        updatedItemConfigurations = existingReport.itemConfigurations.map(
          (item) =>
            item.id === editDataItem.id
              ? { ...payload.itemConfigurations[0], id: item.id }
              : item,
        )
      } else {
        // IF ADDING NEW, APPEND TO EXISTING ITEMS
        updatedItemConfigurations = [
          { ...payload.itemConfigurations[0], id: uniqueId },
          ...(existingReport?.itemConfigurations || []),
        ]
      }

      // CREATE THE UPDATED REPORT
      const updatedReport = {
        ...existingReport,
        itemConfigurations: updatedItemConfigurations,
      }

      // UPDATE THE REPORT IN THE REPORTS ARRAY
      const updatedReports = (reports || []).map((report) =>
        report.id === id ? updatedReport : report,
      )

      const response = await addReport(updatedReports)
      if (response && response?.data) {
        enqueueSnackbar(
          isEditMode ? 'Item berhasil diperbarui' : 'Item berhasil ditambahkan',
          { variant: 'success' },
        )
        toggleModalAddItem()
        refetch()
      }
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ??
          (isEditMode ? 'Item gagal diperbarui' : 'Item gagal ditambahkan'),
        { variant: 'error' },
      )
    }
  }

  //   HANDLE REMOVE BY ITEM CONFIGURATION ID
  const handleSubmitRemoveByItemConfigurationId = async (itemConfigId) => {
    try {
      // FIND THE EXISTING REPORT
      const existingReport = reports?.find((report) => report.id === id)

      // FILTER OUT THE ITEM CONFIGURATION WITH THE MATCHING ID
      const updatedItemConfigurations =
        existingReport.itemConfigurations.filter(
          (item) => item.id !== itemConfigId,
        )

      // CREATE THE UPDATED REPORT WITH FILTERED ITEMS
      const updatedReport = {
        ...existingReport,
        itemConfigurations: updatedItemConfigurations,
      }

      // UPDATE THE REPORT IN THE REPORTS ARRAY
      const updatedReports = (reports || []).map((report) =>
        report.id === id ? updatedReport : report,
      )

      const response = await addReport(updatedReports)
      if (response && response?.data) {
        enqueueSnackbar('Item berhasil dihapus', {
          variant: 'success',
        })
        toggleModalDelete()
        refetch()
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? 'Item gagal dihapus', {
        variant: 'error',
      })
    }
  }

  // COLUMNS
  const columns = [
    {
      name: <span>Jenis</span>,
      selector: 'type',
      grow: 2,
      minWidth: '150px',
      cell: (row) => {
        return <span>{row?.type?.label ?? '-'}</span>
      },
    },
    {
      name: <span>Barang</span>,
      selector: 'commodity',
      grow: 1,
      left: true,
      minWidth: '150px',
      cell: (row) => {
        return (
          <ul style={{ padding: 4, margin: 0 }}>
            {row?.commodity?.label ?? row?.commodity?.commodity?.label ?? '-'}
          </ul>
        )
      },
    },
    {
      name: <span>Volume</span>,
      selector: 'volume',
      right: true,
      minWidth: '90px',
      maxWidth: '90px',
      // grow: 1,
      cell: (row) => {
        return <span>{row?.volume ?? '-'}</span>
      },
    },
    {
      name: <span>Satuan</span>,
      selector: 'unit',
      minWidth: '100px',
      maxWidth: '100px',
      left: true,
      // grow: 1,
      cell: (row) => {
        return <span>{row?.unit?.label ?? '-'}</span>
      },
    },
    {
      name: <span>PSDH</span>,
      selector: 'psdh',
      right: true,
      cell: (row) => {
        return <span>Rp{formatInputNumber(row?.psdh ?? 0)}</span>
      },
    },
    {
      name: '',
      width: '80px',
      left: true,
      cell: (row) => {
        return (
          <div
            className="d-flex align-items-center"
            style={{ gap: '16px', flexGrow: 1 }}
          >
            <div id={`edit-${row?.id}`}>
              <Edit2
                size={18}
                onClick={() => {
                  toggleModalAddItem()
                  setEditDataItem(row)

                  setPayload({
                    id: id,
                    title: findReport?.title,
                    periode: findReport?.periode,
                    status: findReport?.status,
                    itemConfigurations: [
                      {
                        type: row?.type,
                        commodity: row?.commodity,
                        psdh: row?.psdh,
                        unit: row?.unit,
                        volume: row?.volume,
                        total: row?.total,
                      },
                    ],
                  })
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <PMBTooltip target={`edit-${row?.id}`} text="Ubah" />

            <div id={`remove-${row?.id}`}>
              <Trash2
                color="red"
                size={18}
                onClick={() => {
                  setIsOpenDeleteModal(true)
                  setEditDataItem(row)

                  setPayload({
                    id: id,
                    title: findReport?.title,
                    periode: findReport?.periode,
                    status: findReport?.status,
                    itemConfigurations: [
                      {
                        id: row?.id,
                        type: row?.type,
                        commodity: row?.commodity,
                        psdh: row?.psdh,
                        unit: row?.unit,
                        volume: row?.volume,
                        total: row?.total,
                      },
                    ],
                  })
                }}
                style={{ cursor: 'pointer' }}
              />
            </div>
            <PMBTooltip target={`remove-${row?.id}`} text="Hapus" />
          </div>
        )
      },
    },
  ]

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      {/* MODAL LEDGER */}
      <ModalLedger
        isOpen={isOpenModalLedger}
        toggle={toggleModalLedger}
        payload={payload}
        id={id}
        dataConfig={data}
        dataReports={reports}
        refetch={refetch}
        // handleLedgerChange={handleLedgerChange}
      />

      {/* MODAL DELETE */}
      <PMBModalRemove
        isOpen={isOpenDeleteModal}
        onClose={() => setIsOpenDeleteModal((prev) => !prev)}
        title={`Hapus Item`}
        dataId={editDataItem?.id}
        desc={`Apakah Anda yakin ingin menghapus item ${editDataItem?.commodity?.label}`}
        isLoading={isAdding}
        onRemoveData={(id) => handleSubmitRemoveByItemConfigurationId(id)}
        buttonTitle="Hapus"
      />

      {/* MODAL ADD ITEM */}
      <ModalAddItem
        isOpen={isOpenModalAddItem}
        toggle={toggleModalAddItem}
        typeOptions={typeOptions}
        handleSelectType={handleSelectType}
        commodityOptions={commodityOptions}
        payload={payload}
        handleSelectCommodity={handleSelectCommodity}
        handleVolumeChange={handleVolumeChange}
        resetState={resetState}
        isFormInvalid={isFormInvalid}
        handleSubmit={handleSubmit}
        isLoadingAddReport={isAdding}
        isEditMode={isEditMode}
      />

      <div className="mt-3">
        <BreadCrumbs
          breadCrumbTitle="Beranda"
          breadCrumbParent2="Daftar Laporan PNBP"
          breadCrumbParent2Link={URL_REPORT_PNBP}
          breadCrumbActive="Detail Laporan PNBP"
          breadCrumbParent={'Alat Bantu'}
          breadCrumbParentLink={URL_WORKPLACE}
        />
      </div>

      <Card className="border-0 shadow-none">
        <div className="w-100">
          <div className="col-12 col-md-6 d-flex align-items-center mb-2 mb-md-0 px-4 mt-4">
            <ArrowLeft
              size={20}
              className="me-2"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(URL_REPORT_PNBP)}
            />
            <P isBold size="large" className="fw-semibold my-1">
              Detail Laporan PNBP
            </P>
          </div>
        </div>
        <Row className="p-4">
          <Col xs={12} sm={6}>
            <TableCombinedContent />
          </Col>

          <Col xs={12} sm={6}>
            <Card className="mb-3 border-0 shadow-sm">
              <CardBody>
                <DataTable
                  columns={columns}
                  data={findReport?.itemConfigurations ?? []}
                  progressPending={isFetching || isLoading}
                  progressComponent={
                    <div style={{ margin: '100px 0px' }}>
                      <Spinner color="primary" />
                    </div>
                  }
                  subHeader={true}
                  subHeaderComponent={
                    <div className="w-100">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center w-100">
                        <div className="d-flex flex-column mb-3 mb-md-0">
                          {/* <P size="regular" className="fw-semibold">
                            {findReport?.title}
                          </P>
                          <P size="small" className="fw-semibold">
                            {findReport?.periode}
                          </P> */}
                        </div>

                        <div className="btn-scroll-wrapper d-md-flex gap-2">
                          <div className="d-flex flex-nowrap overflow-auto gap-2">
                            <Button
                              onClick={() => {
                                toggleModalLedger()
                                setPayload({
                                  id: findReport?.id,
                                  title: findReport?.title,
                                  periode: findReport?.periode,
                                  status: findReport?.status,
                                  itemConfigurations:
                                    findReport?.itemConfigurations,
                                })
                              }}
                              size="sm"
                              color="primary"
                              outline
                              className="flex-shrink-0"
                              data-testid="pnbp-modal-ledger-open"
                            >
                              <Plus size={14} /> Tambah Arus Kas
                            </Button>
                            <Button
                              onClick={toggleModalAddItem}
                              size="sm"
                              color="primary"
                              className="flex-shrink-0"
                              data-testid="pnbp-add-item-button"
                            >
                              <Plus size={14} /> Tambah
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                  noDataComponent={
                    <p className="my-3 fw-semibold">
                      Belum ada detail laporan pnbp yang tersedia
                    </p>
                  }
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default DetailReportPnbp
